/* eslint-disable no-loops/no-loops */
import { Map } from 'maplibre-gl';
import { useCallback, useEffect } from 'react';
import { isMobile } from 'react-device-detect';
import { useSelector } from 'react-redux';

import { useBoundaryContext } from '@/features/boundaries/provider';
import {
  CustomizeFeature,
  useCustomizeLandContext,
} from '@/features/customizeMyLand/provider';

import { RootState } from '../../redux/reducers';
import useUpdatedRef from '../useUpdatedRef';

/**
 * Handles clicking on parcels and de-selecting features from the map
 * @param map
 */
const useMapCustomizationsSelections = (map: Map | undefined) => {
  const { isDrawing } = useSelector((state: RootState) => state.drawV2);
  const isDrawingRef = useUpdatedRef(isDrawing);

  const {
    setActiveFeature,
    customizations: { allCustomizations },
  } = useCustomizeLandContext();

  const { editMode } = useBoundaryContext();

  /**
   * Used for clicking on the map and adding features to the selections
   */
  const onFeatureClick = useCallback(
    (clickEvent) => {
      if (!map || isDrawingRef.current || editMode) return;

      if (clickEvent.originalEvent.cancelBubble) return;

      if (map?.isZooming() && isMobile) return; // Attempts to fix selection changing when users pinch to zoom on mobile

      const renderedFeatures = map.queryRenderedFeatures(clickEvent.point);

      const containsDrawFeature = renderedFeatures.some((feature) =>
        feature.layer.id.includes('draw')
      );

      if (!containsDrawFeature) {
        return;
      }

      const selectedCustomization = allCustomizations.find(
        (customization) => Number(customization.id) === renderedFeatures[0].id
      );

      if (!selectedCustomization) return;

      setActiveFeature(selectedCustomization as CustomizeFeature);
    },
    [allCustomizations, editMode, isDrawingRef, map, setActiveFeature]
  );

  /**
   * Map Listeners
   */
  useEffect(() => {
    if (!map) return () => {};

    map.on('click', onFeatureClick);

    return () => {
      map.off('click', onFeatureClick);
    };
  }, [map, onFeatureClick]);
};

export default useMapCustomizationsSelections;
